import { TourStep } from '../context/TourContext';

export const onboardingTourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to OpenTender AI!',
    content: 'Get ready for a quick tour of the platform that will help you win more tenders with AI-powered analysis.',
    target: 'body',
    placement: 'center',
    showPrev: false,
  },
  {
    id: 'dashboard-nav',
    title: 'Dashboard - Your Command Center',
    content: 'This is your main dashboard where you can see an overview of all your analyses, metrics, and recent activity.',
    target: 'a[href="/"]',
    placement: 'bottom',
    action: () => {
      const navLink = document.querySelector('a[href="/"]') as HTMLElement;
      if (navLink) {
        navLink.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.5)';
        navLink.style.borderRadius = '8px';
      }
    },
  },
  {
    id: 'tender-response-nav',
    title: 'Tender Response - AI-Powered Generation',
    content: 'Generate or refine tender responses here. Our AI creates responses using your knowledge base and successful tender patterns.',
    target: 'a[href="/tender-response"]',
    placement: 'bottom',
    action: () => {
      const navLink = document.querySelector('a[href="/tender-response"]') as HTMLElement;
      if (navLink) {
        navLink.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.5)';
        navLink.style.borderRadius = '8px';
      }
    },
  },
  {
    id: 'projects-nav',
    title: 'Projects - Organize Your Work',
    content: 'Manage your tender projects, track progress, and collaborate with team members on ongoing submissions.',
    target: 'a[href="/projects"]',
    placement: 'bottom',
    action: () => {
      const navLink = document.querySelector('a[href="/projects"]') as HTMLElement;
      if (navLink) {
        navLink.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.5)';
        navLink.style.borderRadius = '8px';
      }
    },
  },
  {
    id: 'tender-bank-nav',
    title: 'Tender Bank - Your Knowledge Base',
    content: 'Access your library of successful tenders, templates, and reference materials to improve future submissions.',
    target: 'a[href="/tender-bank"]',
    placement: 'bottom',
    action: () => {
      const navLink = document.querySelector('a[href="/tender-bank"]') as HTMLElement;
      if (navLink) {
        navLink.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.5)';
        navLink.style.borderRadius = '8px';
      }
    },
  },
  {
    id: 'profile-nav',
    title: 'Profile - Manage Your Account',
    content: 'Access your profile information, update personal details, and manage your account preferences.',
    target: 'a[href="/profile"]',
    placement: 'bottom',
    action: () => {
      const navLink = document.querySelector('a[href="/profile"]') as HTMLElement;
      if (navLink) {
        navLink.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.5)';
        navLink.style.borderRadius = '8px';
      }
    },
  },
  {
    id: 'settings-nav',
    title: 'Settings - Customize Your Experience',
    content: 'Personalize your OpenTender AI experience with theme, notification, and workflow preferences.',
    target: 'a[href="/settings"]',
    placement: 'bottom',
    action: () => {
      const navLink = document.querySelector('a[href="/settings"]') as HTMLElement;
      if (navLink) {
        navLink.style.boxShadow = '0 0 0 3px rgba(236, 72, 153, 0.5)';
        navLink.style.borderRadius = '8px';
      }
    },
  },
  {
    id: 'try-tender-response',
    title: 'Ready to Start?',
    content: 'Click on Tender Response to upload your first tender document and generate AI-powered responses!',
    target: 'a[href="/tender-response"]',
    placement: 'bottom',
    action: () => {
      const navLink = document.querySelector('a[href="/tender-response"]') as HTMLElement;
      if (navLink) {
        navLink.style.animation = 'pulse 2s infinite';
        navLink.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.8)';
      }
    },
  },
  {
    id: 'completion',
    title: 'You\'re All Set!',
    content: 'Congratulations! You now know how to navigate the platform. Explore each section to discover all the powerful features at your disposal.',
    target: 'body',
    placement: 'center',
    showSkip: false,
    action: () => {
      // Clean up any lingering styles from all navigation links
      const navLinks = document.querySelectorAll('nav a, a[href="/"], a[href="/tender-response"], a[href="/projects"], a[href="/tender-bank"], a[href="/profile"], a[href="/settings"]');
      navLinks.forEach(link => {
        const el = link as HTMLElement;
        el.style.boxShadow = '';
        el.style.animation = '';
        el.style.borderRadius = '';
      });
    },
  },
];

export const dashboardTourSteps: TourStep[] = [
  {
    id: 'dashboard-welcome',
    title: 'Welcome to Your Dashboard',
    content: 'This is your command center for managing tender analyses and tracking your success.',
    target: '.dashboard-header',
    placement: 'bottom',
    showPrev: false,
  },
  {
    id: 'metrics-overview',
    title: 'Performance Metrics',
    content: 'Track your success with these key metrics: total analyses, active projects, success rate, and time saved.',
    target: '.metrics-grid',
    placement: 'bottom',
  },
  {
    id: 'quick-actions',
    title: 'Quick Actions',
    content: 'Access frequently used features quickly: start new analysis, upload documents, start new evaluation, and settings.',
    target: '.quick-actions-panel',
    placement: 'left',
  },
  {
    id: 'recent-activity',
    title: 'Recent Activity',
    content: 'Stay updated with your latest analyses, uploads, and system updates.',
    target: '.recent-activity-section',
    placement: 'top',
  },
  {
    id: 'global-opportunities',
    title: 'Global Insights',
    content: 'Visualize worldwide tender distribution and opportunities in your market.',
    target: '.globe-section',
    placement: 'left',
  },
  {
    id: 'new-tender-response-form',
    title: 'Start New Tender Response',
    content: 'Upload tender documents here to generate or refine AI-powered responses.',
    target: '#new-analysis-form',
    placement: 'top',
  },
];

export const analysisPageTourSteps: TourStep[] = [
  {
    id: 'tender-response-welcome',
    title: 'Tender Response Workspace',
    content: 'This is where the magic happens! Upload documents and watch our AI generate responses in real-time.',
    target: '.analysis-container',
    placement: 'center',
    showPrev: false,
  },
  {
    id: 'file-upload',
    title: 'Upload Documents',
    content: 'Drag and drop or browse to upload tender documents. We support PDF, Word, and many other formats.',
    target: '.file-upload-area',
    placement: 'bottom',
  },
  {
    id: 'response-options',
    title: 'Response Options',
    content: 'Choose to generate new responses or refine existing ones. Configure output preferences.',
    target: '.analysis-options',
    placement: 'right',
  },
  {
    id: 'progress-tracking',
    title: 'Real-Time Progress',
    content: 'Watch as our AI processes your documents step by step, from extraction to response generation.',
    target: '.progress-section',
    placement: 'bottom',
  },
];

// Helper function to get tour steps based on context
export const getTourSteps = (context: 'onboarding' | 'dashboard' | 'analysis'): TourStep[] => {
  switch (context) {
    case 'onboarding':
      return onboardingTourSteps;
    case 'dashboard':
      return dashboardTourSteps;
    case 'analysis':
      return analysisPageTourSteps;
    default:
      return onboardingTourSteps;
  }
};